import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const EventContainer = styled.section`
  padding: 6rem 0;
  background: ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 3rem 0;
  }
`;

const EventHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
`;

const EventTitle = styled(motion.h2)`
  font-size: 3rem;
  margin-bottom: 1rem;
  background: ${({ theme }) => theme.gradients.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const EventSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    padding: 0 1rem;
  }
`;

const VenueGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const VenueCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${({ theme }) => theme.gradients.primary};
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover::before {
    transform: scaleX(1);
  }
  
  &:hover {
    transform: translateY(-5px);
    border-color: ${({ theme }) => theme.colors.primary.blue};
    box-shadow: 0 15px 30px rgba(0, 128, 214, 0.1);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
    margin: 0 1rem;
  }
`;

const VenueIcon = styled.div`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.primary.yellow};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }
`;

const VenueCardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.primary.blue};
  font-weight: 600;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.3rem;
    margin-bottom: 0.75rem;
  }
`;

const VenueCardContent = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
  
  strong {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  
  .highlight {
    color: ${({ theme }) => theme.colors.primary.yellow};
    font-weight: 500;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.95rem;
  }
`;

const ParkingSection = styled(motion.div)`
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 20px;
  padding: 3rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(10px);
  margin-bottom: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    border-radius: 15px;
    padding: 1.5rem;
    margin: 0 1rem 2rem 1rem;
  }
`;

const ParkingHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 1.5rem;
  }
`;

const ParkingTitle = styled.h3`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary.yellow};
  margin-bottom: 1rem;
  font-weight: 700;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
`;

const ParkingSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    padding: 0 0.5rem;
  }
`;

const ParkingContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const ParkingMapContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 15px;
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1rem;
    border-radius: 10px;
  }
`;

const ParkingMapTitle = styled.h4`
  color: ${({ theme }) => theme.colors.primary.blue};
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const ParkingMapImage = styled(motion.img)`
  width: 100%;
  max-width: 500px;
  height: auto;
  border-radius: 10px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.yellow};
    box-shadow: 0 8px 25px rgba(255, 229, 0, 0.2);
    transform: scale(1.02);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    border-radius: 8px;
    border-width: 1px;
    
    &:hover {
      transform: none;
      box-shadow: 0 4px 15px rgba(255, 229, 0, 0.15);
    }
  }
`;

const ParkingNote = styled.div`
  background: rgba(255, 229, 0, 0.1);
  border: 1px solid ${({ theme }) => theme.colors.primary.yellow};
  border-radius: 10px;
  padding: 1.5rem;
  margin-top: 2rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.95rem;
  text-align: left;
  line-height: 1.6;
  
  strong {
    color: ${({ theme }) => theme.colors.primary.yellow};
  }
  
  ul {
    margin: 1rem 0 0 0;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.5rem;
      
      strong {
        color: ${({ theme }) => theme.colors.text.primary};
      }
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1rem;
    margin-top: 1rem;
    font-size: 0.9rem;
    border-radius: 8px;
    
    ul {
      padding-left: 1rem;
      margin: 0.75rem 0 0 0;
      
      li {
        margin-bottom: 0.4rem;
        line-height: 1.5;
      }
    }
  }
`;

const ScheduleSection = styled.div`
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 20px;
  padding: 3rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(10px);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    border-radius: 15px;
    padding: 1.5rem;
    margin: 0 1rem;
  }
`;

const ScheduleHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 1.5rem;
  }
`;

const ScheduleTitle = styled.h3`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary.yellow};
  margin-bottom: 1rem;
  font-weight: 700;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
`;

const ScheduleSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.95rem;
    padding: 0 0.5rem;
  }
`;

const FilterContainer = styled.div`
  margin-bottom: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 1.5rem;
  }
`;

const ScheduleTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const ScheduleTab = styled(motion.button)`
  padding: 1rem 2rem;
  background: ${({ active, theme }) => active ? theme.colors.primary.blue : theme.colors.surface};
  border: 2px solid ${({ active, theme }) => active ? theme.colors.primary.blue : theme.colors.border};
  border-radius: 25px;
  color: ${({ active, theme }) => active ? theme.colors.text.primary : theme.colors.text.secondary};
  cursor: pointer;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.blue};
    background: ${({ theme }) => theme.colors.primary.blue};
    color: ${({ theme }) => theme.colors.text.primary};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
    letter-spacing: 0.5px;
    width: 80%;
    max-width: 250px;
  }
`;

const RoomFilterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 1rem;
  }
`;

const RoomFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 0.4rem;
  }
`;

const RoomFilterLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.9rem;
  font-weight: 500;
  margin-right: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.8rem;
    margin-right: 0.3rem;
    margin-bottom: 0.25rem;
  }
`;

const RoomFilter = styled(motion.button)`
  padding: 0.5rem 1rem;
  background: ${({ active, theme }) => active ? theme.colors.primary.yellow : theme.colors.surface};
  border: 1px solid ${({ active, theme }) => active ? theme.colors.primary.yellow : theme.colors.border};
  border-radius: 20px;
  color: ${({ active, theme }) => active ? theme.colors.background.primary : theme.colors.text.secondary};
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.yellow};
    background: ${({ theme }) => theme.colors.primary.yellow};
    color: ${({ theme }) => theme.colors.background.primary};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.4rem 0.8rem;
    font-size: 0.75rem;
  }
`;

const ActiveFiltersContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  min-height: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 1.2rem;
    margin-bottom: 0.75rem;
  }
`;

const ActiveFilter = styled.span`
  background: rgba(255, 229, 0, 0.2);
  color: ${({ theme }) => theme.colors.primary.yellow};
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.75rem;
    padding: 0.2rem 0.6rem;
  }
`;

const ScheduleContent = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
`;

const ScheduleDay = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 15px;
  padding: 2rem;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.blue};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1rem;
    border-radius: 10px;
    border-left-width: 3px;
  }
`;

const DayHeader = styled.div`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary.yellow};
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 600;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const TimelineItem = styled(motion.div)`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 1.5rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: rgba(255, 229, 0, 0.05);
    border-radius: 8px;
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    text-align: center;
    padding: 0.75rem 0;
    
    &:hover {
      padding-left: 0;
      padding-right: 0;
      padding-top: 0.75rem;
      padding-bottom: 0.75rem;
    }
  }
`;

const TimelineTime = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  color: ${({ theme }) => theme.colors.primary.blue};
  font-weight: 600;
  font-size: 0.95rem;
  background: rgba(0, 128, 214, 0.1);
  padding: 0.5rem;
  border-radius: 8px;
  text-align: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    padding: 0.4rem;
    border-radius: 6px;
    width: fit-content;
    margin: 0 auto;
  }
`;

const TimelineEvent = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1rem;
  
  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
  
  .speaker {
    color: ${({ theme }) => theme.colors.primary.yellow};
    font-style: italic;
    font-size: 0.9rem;
    margin-top: 0.25rem;
  }
  
  .room {
    color: ${({ theme }) => theme.colors.primary.blue};
    font-weight: 600;
    font-size: 0.85rem;
    background: rgba(0, 128, 214, 0.1);
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    margin-top: 0.5rem;
    display: inline-block;
  }
  
  .room-description {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 0.8rem;
    margin-top: 0.25rem;
    font-style: italic;
    opacity: 0.8;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.95rem;
    
    .speaker {
      font-size: 0.85rem;
      margin-top: 0.2rem;
    }
    
    .room {
      font-size: 0.8rem;
      padding: 0.15rem 0.4rem;
      margin-top: 0.4rem;
    }
    
    .room-description {
      font-size: 0.75rem;
      margin-top: 0.2rem;
    }
  }
`;

const NoEventsMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-style: italic;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 2rem 1rem;
    font-size: 0.9rem;
  }
`;

const EventDetails = () => {
  const [activeScheduleDay, setActiveScheduleDay] = useState(1);
  const [selectedRoom, setSelectedRoom] = useState('all');

  const venueInfo = [
    {
      icon: "📅",
      title: "Date & Time",
      content: (
        <>
          <strong>July 25-26, 2025</strong><br />
          Friday & Saturday<br />
          8:00 AM - 5:00 PM
        </>
      )
    },
    {
      icon: "📍",
      title: "Location",
      content: (
        <>
          <strong>Albuquerque, New Mexico</strong><br />
          UNM Continuing Education - Conference Center<br />
        </>
      )
    },
    {
      icon: "🎟️",
      title: "Registration",
      content: (
        <>
          <strong>Early Bird Discounts</strong><br />
          $0 – $23.18<br />
          <span className="highlight">Limited seats available</span>
        </>
      )
    }
  ];

  const scheduleData = {
  1: [
    { time: "8:00 AM", event: "Registration/Check-In and Breakfast", speaker: "Welcome coffee & networking", room: "Main Auditorium", roomDescription: "Registration desk & continental breakfast (60 min)" },
    { time: "8:00 AM", event: "Breakfast", speaker: "Continental breakfast", room: "Room C", roomDescription: "Dining Hall/Quiet Space (60 min)" },
    { time: "9:00 AM", event: "Welcoming Remarks", speaker: "Conference organizers", room: "Main Auditorium", roomDescription: "Opening ceremony & introductions (60 min)" },
    { time: "10:00 AM", event: "Keynote", speaker: "Mari Galloway", room: "Main Auditorium", roomDescription: "Main presentation hall (30 min)" },
    { time: "10:30 AM", event: "Break", speaker: "Networking opportunity", room: "Main Auditorium", roomDescription: "Coffee & light refreshments (15 min)" },
    { time: "10:45 AM", event: "Your Secrets Are Showing: What Hackers Can Find Online", speaker: "Connar McCasland", room: "Main Auditorium", roomDescription: "OSINT & privacy security session (30 min)" },
    { time: "10:45 AM", event: "Global One Defense", speaker: "Industry presentation", room: "Room B", roomDescription: "Healthcare Track/Career Track (45 min)" },
    { time: "10:45 AM", event: "Snacks/Quiet Space", speaker: "Networking area", room: "Room C", roomDescription: "Dining Hall/Quiet Space (90 min)" },
    { time: "10:45 AM", event: "Cyber Skills Lab", speaker: "Hands-on training", room: "Room D", roomDescription: "Cyberskills Lab (until 5:15 pm)" },
    { time: "10:45 AM", event: "PlexTrac Workshop", speaker: "PlexTrac", room: "Room 123", roomDescription: "Knowledge Hub (45 min)" },
    { time: "10:45 AM", event: "CTF", speaker: "Capture the Flag competition", room: "Room 124", roomDescription: "CTF (until 5:15 pm)" },
    { time: "10:45 AM", event: "Soldering and Lock Picking", speaker: "Hands-on village", room: "Room 125", roomDescription: "Soldering/Lock Picking Village (until 5:15 pm)" },
    { time: "11:15 AM", event: "Cultural Firewalls: Bridging the Gap Between Nonprofits, Arts Organizations, and Cybersecurity", speaker: "Denise Zubizarreta", room: "Main Auditorium", roomDescription: "Cultural cybersecurity insights (30 min)" },
    { time: "11:30 AM", event: "Current and Emerging Threat Trends in the Health Sector", speaker: "Ethan Muntz", room: "Room B", roomDescription: "Healthcare Track/Career Track (45 min)" },
    { time: "11:30 AM", event: "KC7: Cyber Detection Game", speaker: "KC7 Team", room: "Room 123", roomDescription: "Knowledge Hub (45 min)" },
    { time: "11:45 AM", event: "The Human Cost of Cyber Attacks: Beyond the Financial Impacts", speaker: "Jacob Wellnitz", room: "Main Auditorium", roomDescription: "Impact analysis & case studies (30 min)" },
    { time: "12:15 PM", event: "Lunch Break", speaker: "Networking & sponsor expo", room: "Main Auditorium", roomDescription: "Main dining area (45 min)" },
    { time: "12:15 PM", event: "Lunch", speaker: "Dining service", room: "Room C", roomDescription: "Dining Hall/Quiet Space (45 min)" },
    { time: "1:00 PM", event: "CISO/Leadership Panel", speaker: "Kristen Sanders", room: "Main Auditorium", roomDescription: "Executive leadership discussion (45 min)" },
    { time: "1:00 PM", event: "Break for Leadership Panel", speaker: "Panel break", room: "Room B", roomDescription: "Healthcare Track/Career Track (45 min)" },
    { time: "1:00 PM", event: "Snacks/Quiet Space", speaker: "Afternoon networking", room: "Room C", roomDescription: "Dining Hall/Quiet Space (until 5:15 pm)" },
    { time: "1:00 PM", event: "From Theory to Practice: Building and Assessing Blue Team Playbooks", speaker: "Jack Callaway", room: "Room 123", roomDescription: "Knowledge Hub (45 min)" },
    { time: "1:45 PM", event: "Just because you can, doesn't mean you should!", speaker: "Mark Orgeron", room: "Main Auditorium", roomDescription: "Ethics in cybersecurity (45 min)" },
    { time: "1:45 PM", event: "Your Extended Team when calling a code", speaker: "Phillip Smith", room: "Room B", roomDescription: "Healthcare Track/Career Track (45 min)" },
    { time: "2:30 PM", event: "Break", speaker: "Coffee & networking", room: "Main Auditorium", roomDescription: "15 minute refreshment break" },
    { time: "2:45 PM", event: "Trailblazing: Lessons from Oregon Trail for the Secure Software Supply Chain", speaker: "Kadi McKean", room: "Main Auditorium", roomDescription: "Supply chain security insights (30 min)" },
    { time: "2:45 PM", event: "Unwanted Cyber Guest", speaker: "Raphael Warren", room: "Room B", roomDescription: "Healthcare Track/Career Track (45 min)" },
    { time: "2:45 PM", event: "Continuous Integration, Continuous Exfiltration", speaker: "J. B. Crawford", room: "Room 123", roomDescription: "Knowledge Hub (45 min)" },
    { time: "3:15 PM", event: "Enhancing Cybersecurity Intelligence with Retrieval-Augmented Generation (RAG)", speaker: "Shaikh Ahmad", room: "Main Auditorium", roomDescription: "AI security & RAG implementation (30 min)" },
    { time: "3:30 PM", event: "Chaos Navigation: Mastering Preparedness for Success in Incident Response", speaker: "Christina Barker", room: "Room B", roomDescription: "Healthcare Track/Career Track (45 min)" },
    { time: "3:30 PM", event: "How I found a 0 day using Inversion Theory", speaker: "Shubham Khichi", room: "Room 123", roomDescription: "Knowledge Hub (45 min)" },
    { time: "3:45 PM", event: "CMMC - Not an \"IT\" Certification", speaker: "Dy Edington", room: "Main Auditorium", roomDescription: "Compliance & certification guidance (30 min)" },
    { time: "4:15 PM", event: "Break", speaker: "Final networking break", room: "Main Auditorium", roomDescription: "15 minute refreshment break" },
    { time: "4:30 PM", event: "Cyber Defenders: Turning Skills Into Lifesaving Action", speaker: "Kristen Sanders", room: "Main Auditorium", roomDescription: "Career development & skills (45 min)" },
    { time: "4:30 PM", event: "Building Rural Health Resiliency with Cybersecurity and AI", speaker: "Laura Kreofsky", room: "Room B", roomDescription: "Healthcare Track/Career Track (45 min)" }
  ],
  2: [
    { time: "8:00 AM", event: "Registration/Check-In and Breakfast", speaker: "Day 2 welcome & breakfast", room: "Main Auditorium", roomDescription: "Registration desk & continental breakfast (60 min)" },
    { time: "8:00 AM", event: "Breakfast", speaker: "Continental breakfast", room: "Room C", roomDescription: "Dining Hall/Quiet Space (60 min)" },
    { time: "9:00 AM", event: "Keynote", speaker: "Mark Fidel", room: "Main Auditorium", roomDescription: "Day 2 opening keynote (45 min)" },
    { time: "9:45 AM", event: "Prompt Engineering and Injection: Fundamental and Advanced Techniques", speaker: "Micah Turner", room: "Main Auditorium", roomDescription: "AI security & prompt injection (45 min)" },
    { time: "9:45 AM", event: "Resume Review and Networking", speaker: "Career development session", room: "Room B", roomDescription: "Healthcare Track/Career Track (until 3:40 PM)" },
    { time: "9:45 AM", event: "Snacks/Quiet Space", speaker: "Morning networking", room: "Room C", roomDescription: "Dining Hall/Quiet Space (until 12:15 PM)" },
    { time: "9:45 AM", event: "Cyber Skills Lab", speaker: "Hands-on training", room: "Room D", roomDescription: "Cyberskills Lab (until 3:40 PM)" },
    { time: "9:45 AM", event: "Attacking LLM-Powered Email Summarizers", speaker: "Mohamed AbuMuslim", room: "Room 123", roomDescription: "Knowledge Hub (45 min)" },
    { time: "9:45 AM", event: "CTF", speaker: "Capture the Flag competition", room: "Room 124", roomDescription: "CTF (until 3:40 PM)" },
    { time: "9:45 AM", event: "Soldering and Lock Picking", speaker: "Hands-on village", room: "Room 125", roomDescription: "Soldering/Lock Picking Village (until 3:40 PM)" },
    { time: "10:30 AM", event: "Break", speaker: "Coffee & networking", room: "Main Auditorium", roomDescription: "15 minute refreshment break" },
    { time: "10:45 AM", event: "Vulnerabilities and Defenses for Distributed Energy and EV Charging", speaker: "Jay Johnson", room: "Main Auditorium", roomDescription: "Critical infrastructure security (30 min)" },
    { time: "10:45 AM", event: "SecTopRat / Arechclient2 – The latest on this popular malware family", speaker: "Michael Gough", room: "Room 123", roomDescription: "Knowledge Hub (45 min)" },
    { time: "11:15 AM", event: "Resisting Censorship Attacks from a Nation-State Threat", speaker: "Alex Adams", room: "Main Auditorium", roomDescription: "Nation-state threat analysis (30 min)" },
    { time: "11:30 AM", event: "Hacking a backup power solution(s) for your home, Tornado tested! - 40 min", speaker: "Michael Gough", room: "Room 123", roomDescription: "Knowledge Hub (45 min)" },
    { time: "11:45 AM", event: "Pathways to Pentesting – thoughts on becoming a professional penetration tester.", speaker: "Russ Gritzo", room: "Main Auditorium", roomDescription: "Career guidance for penetration testing (30 min)" },
    { time: "12:15 PM", event: "Lunch Break", speaker: "Networking & sponsor expo", room: "Main Auditorium", roomDescription: "Main dining area (45 min)" },
    { time: "12:15 PM", event: "Lunch", speaker: "Dining service", room: "Room C", roomDescription: "Dining Hall/Quiet Space (45 min)" },
    { time: "1:00 PM", event: "Navigating the Virtualization Battlefield: A Deep Dive into Hypervisor Attack Vectors", speaker: "Austin Gadient", room: "Main Auditorium", roomDescription: "Virtualization security deep dive (30 min)" },
    { time: "1:00 PM", event: "Snacks/Quiet Space", speaker: "Afternoon networking", room: "Room C", roomDescription: "Dining Hall/Quiet Space (until 3:40 PM)" },
    { time: "1:00 PM", event: "Deep Dive OSINT: Techniques for Finding Hidden Data", speaker: "Christopher De La Rosa", room: "Room 123", roomDescription: "Knowledge Hub (until 3:40 PM)" },
    { time: "1:30 PM", event: "Maximum Effort: How to be a Cybersecurity Superhero", speaker: "Rebecca Rutherford", room: "Main Auditorium", roomDescription: "Career motivation & skills development (30 min)" },
    { time: "2:00 PM", event: "The Rise of Remote Management Tool Abuse: Introducing the AutoRMM Testing Framework", speaker: "Mike Manrod, Ezra Woods", room: "Main Auditorium", roomDescription: "RMM security testing framework (30 min)" },
    { time: "2:25 PM", event: "Break", speaker: "Final networking break", room: "Main Auditorium", roomDescription: "15 minute refreshment break" },
    { time: "2:40 PM", event: "Chart Your Own Path: Navigating Cybersecurity Certifications", speaker: "Connar McCasland", room: "Main Auditorium", roomDescription: "Certification guidance & career planning (30 min)" },
    { time: "3:10 PM", event: "Ashley Talk", speaker: "Ashley", room: "Main Auditorium", roomDescription: "Special closing presentation (30 min)" },
    { time: "3:40 PM", event: "Closing Remarks", speaker: "Conference organizers", room: "Main Auditorium", roomDescription: "Conference wrap-up & thank you (20 min)" },
    { time: "6:00 PM", event: "After Party", speaker: "Drinks, food & networking", room: "Main Auditorium", roomDescription: "2 hour celebration event (120 min)" }
  ]
};

  // Get unique rooms for filtering
  const getRoomsForDay = (day) => {
    const rooms = new Set();
    scheduleData[day].forEach(item => rooms.add(item.room));
    return Array.from(rooms).sort();
  };

  // Filter events based on selected room
  const getFilteredEvents = () => {
    const dayEvents = scheduleData[activeScheduleDay];
    if (selectedRoom === 'all') {
      return dayEvents;
    }
    return dayEvents.filter(event => event.room === selectedRoom);
  };

  // Get room display name
  const getRoomDisplayName = (room) => {
    const roomNames = {
      'Main Auditorium': 'Main',
      'Room B': 'Room B',
      'Room C': 'Room C', 
      'Room 123': 'R-123',
      'Room 124': 'R-124',
      'Room 125': 'R-125',
      'Room D': 'Room D'
    };
    return roomNames[room] || room;
  };

  const handleMapClick = () => {
    window.open('/parking.png', '_blank');
  };

  const filteredEvents = getFilteredEvents();
  const availableRooms = getRoomsForDay(activeScheduleDay);

  return (
    <EventContainer id="event">
      <div className="container">
        <EventHeader>
          <EventTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Event Details
          </EventTitle>
          <EventSubtitle>
            Join us for two days of cybersecurity excellence in the heart of New Mexico
          </EventSubtitle>
        </EventHeader>
        
        <VenueGrid>
          {venueInfo.map((venue, index) => (
            <VenueCard
              key={venue.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              <VenueIcon>{venue.icon}</VenueIcon>
              <VenueCardTitle>{venue.title}</VenueCardTitle>
              <VenueCardContent>{venue.content}</VenueCardContent>
            </VenueCard>
          ))}
        </VenueGrid>

        <ParkingSection
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <ParkingHeader>
            <ParkingTitle>🅿️ Parking Information</ParkingTitle>
            <ParkingSubtitle>
              Convenient parking options available near the conference venue
            </ParkingSubtitle>
          </ParkingHeader>

          <ParkingContent>
            <ParkingMapContainer>
              <ParkingMapTitle>
                🗺️ Venue Parking Map
              </ParkingMapTitle>
              <ParkingMapImage
                src="/parking.png"
                alt="Security BSides Albuquerque Conference Center Parking Map"
                onClick={handleMapClick}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              />
              <ParkingNote>
                <strong>📍 Parking Information for UNM Continuing Education Conference Center</strong>
                <ul>
                  <li><strong>UNM Lot K (ELKS):</strong> Overflow parking available when west and east lots are full</li>
                  <li><strong>East Parking Lot:</strong> Primary parking area closest to conference center entrance</li>
                  <li><strong>West Parking Lot:</strong> Secondary parking with easy access to University Blvd</li>
                  <li><strong>Reserved Parking:</strong> Available for accessibility needs and VIP attendees</li>
                  <li><strong>Note:</strong> Masons Lodge area has no parking available</li>
                </ul>
                <br />
                <strong>💡 Recommendations:</strong> Arrive early for best spots. East lot fills up quickly during conference hours. 
                Click the map above to view full-size details.
              </ParkingNote>
            </ParkingMapContainer>
          </ParkingContent>
        </ParkingSection>
        
        <ScheduleSection id="schedule">
          <ScheduleHeader>
            <ScheduleTitle>Conference Schedule</ScheduleTitle>
            <ScheduleSubtitle>Detailed agenda for both days with room filtering</ScheduleSubtitle>
          </ScheduleHeader>
          
          <FilterContainer>
            <ScheduleTabs>
              <ScheduleTab
                active={activeScheduleDay === 1}
                onClick={() => {
                  setActiveScheduleDay(1);
                  setSelectedRoom('all');
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Day 1 - July 25
              </ScheduleTab>
              <ScheduleTab
                active={activeScheduleDay === 2}
                onClick={() => {
                  setActiveScheduleDay(2);
                  setSelectedRoom('all');
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Day 2 - July 26
              </ScheduleTab>
            </ScheduleTabs>
            
            <RoomFilterContainer>
              <RoomFilters>
                <RoomFilterLabel>Filter by Room:</RoomFilterLabel>
                <RoomFilter
                  active={selectedRoom === 'all'}
                  onClick={() => setSelectedRoom('all')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  All Rooms
                </RoomFilter>
                {availableRooms.map((room) => (
                  <RoomFilter
                    key={room}
                    active={selectedRoom === room}
                    onClick={() => setSelectedRoom(room)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {getRoomDisplayName(room)}
                  </RoomFilter>
                ))}
              </RoomFilters>
            </RoomFilterContainer>
            
            <ActiveFiltersContainer>
              {selectedRoom !== 'all' && (
                <ActiveFilter>
                  Showing: {selectedRoom}
                </ActiveFilter>
              )}
            </ActiveFiltersContainer>
          </FilterContainer>
          
          <ScheduleContent
            key={`${activeScheduleDay}-${selectedRoom}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ScheduleDay>
              <DayHeader>
                {activeScheduleDay === 1 ? "Friday, July 25" : "Saturday, July 26"}
                {selectedRoom !== 'all' && ` - ${selectedRoom}`}
              </DayHeader>
              
              {filteredEvents.length > 0 ? (
                filteredEvents.map((item, index) => (
                  <TimelineItem
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    whileHover={{ scale: 1.01 }}
                  >
                    <TimelineTime>{item.time}</TimelineTime>
                    <TimelineEvent>
                      <strong>{item.event}</strong>
                      <div className="speaker">{item.speaker}</div>
                      <div className="room">{item.room}</div>
                      <div className="room-description">{item.roomDescription}</div>
                    </TimelineEvent>
                  </TimelineItem>
                ))
              ) : (
                <NoEventsMessage>
                  No events found for {selectedRoom} on this day.
                </NoEventsMessage>
              )}
            </ScheduleDay>
          </ScheduleContent>
        </ScheduleSection>
      </div>
    </EventContainer>
  );
};

export default EventDetails;